import { Static, Type } from '@sinclair/typebox';
import { StringEnumType } from './string-enum-type';

/**
 * @see https://datatracker.ietf.org/doc/html/rfc7807#section-3.1
 */
export const HttpErrorDetailsBaseSchema = Type.Object({
  object: Type.Literal('Error'),
  type: Type.String(),
  status: Type.Number(),
  title: Type.String(),
  detail: Type.Optional(Type.String()),
  req_id: Type.String(),
});

export const BadRequestResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('BadRequestResponseEnum', [
        'api/invalid_parameters',
        'provider/invalid_parameters',
        'api/invalid_auth_format',
      ]),
      status: Type.Literal(400),
    }),
  ],
  { title: 'BadRequestResponse' },
);

export const UnauthorizedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('UnauthorizedResponseEnum', [
        'provider/invalid_authorization',
        'provider/invalid_credentials',
        'provider/account_mismatch',
        'provider/invalid_checkpoint_code',
        'api/missing_authorization',
        'api/expired_authorization',
        'api/invalid_credentials',
        'api/invalid_hosted_auth_domain',
        'api/proxy_auth_error',
      ]),
      status: Type.Literal(401),
    }),
  ],
  { title: 'UnauthorizedResponse' },
);

export const PaymentRequiredResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('PaymentRequiredResponseEnum', ['provider/payment_required']),
      status: Type.Literal(402),
    }),
  ],
  { title: 'PaymentRequiredResponse' },
);

export const ForbiddenResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('ForbiddenResponseEnum', [
        'api/inactive_subscription',
        'api/insufficient_permissions',
        'api/mfa_required',
        'api/reauthentication_required',
        'api/account_restricted',
        'api/already_exists',
        'api/invalid_turnstile',
        'provider/insufficient_permissions',
        'provider/access_restricted',
        'provider/unknown_authentication_context',
        'provider/password_reset_required',
      ]),
      status: Type.Literal(403),
    }),
  ],
  { title: 'ForbiddenResponse' },
);

export const NotFoundResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('NotFoundResponseEnum', ['provider/resource_not_found', 'api/resource_not_found']),
      status: Type.Literal(404),
    }),
  ],
  { title: 'NotFoundResponse' },
);

export const MethodNotAllowedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('MethodNotAllowedResponseEnum', ['provider/method_not_allowed']),
      status: Type.Literal(405),
    }),
  ],
  { title: 'MethodNotAllowedResponse' },
);

export const NotAcceptableResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('NotAcceptableResponseEnum', ['provider/not_acceptable']),
      status: Type.Literal(406),
    }),
  ],
  { title: 'NotAcceptableResponse' },
);

export const ProxyAuthenticationRequiredResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('ProxyAuthenticationRequiredResponseEnum', ['provider/proxy_authentication_required']),
      status: Type.Literal(407),
    }),
  ],
  { title: 'ProxyAuthenticationRequiredResponse' },
);

export const RequestTimeoutResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('RequestTimeoutResponseEnum', ['provider/request_timeout']),
      status: Type.Literal(408),
    }),
  ],
  { title: 'RequestTimeoutResponse' },
);

export const ConflictResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('ConflictResponseEnum', [
        'api/conflict',
        'api/account_limit_reached',
        'provider/conflict',
        'provider/session_conflict',
      ]),
      status: Type.Literal(409),
    }),
  ],
  { title: 'ConflictResponse' },
);

export const GoneResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('GoneResponseEnum', ['provider/gone']),
      status: Type.Literal(410),
    }),
  ],
  { title: 'GoneResponse' },
);

export const LengthRequiredResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('LengthRequiredResponseEnum', ['provider/length_required']),
      status: Type.Literal(411),
    }),
  ],
  { title: 'LengthRequiredResponse' },
);

export const PreconditionFailedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('PreconditionFailedResponseEnum', ['provider/precondition_failed']),
      status: Type.Literal(412),
    }),
  ],
  { title: 'PreconditionFailedResponse' },
);

export const ContentTooLargeResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('ContentTooLargeResponseEnum', ['provider/content_too_large']),
      status: Type.Literal(413),
    }),
  ],
  { title: 'ContentTooLargeResponse' },
);

export const UriTooLongResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('UriTooLongResponseEnum', ['provider/uri_too_long']),
      status: Type.Literal(414),
    }),
  ],
  { title: 'UriTooLongResponse' },
);

export const UnsupportedMediaTypeResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('UnsupportedMediaTypeResponseEnum', ['provider/invalid_file', 'api/unsupported_content_type']),
      status: Type.Literal(415),
    }),
  ],
  { title: 'UnsupportedMediaTypeResponse' },
);

export const RangeNotSatisfiableResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('RangeNotSatisfiableResponseEnum', ['provider/range_not_satisfiable']),
      status: Type.Literal(416),
    }),
  ],
  { title: 'RangeNotSatisfiableResponse' },
);

export const ExpectationFailedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('ExpectationFailedResponseEnum', ['provider/expectation_failed']),
      status: Type.Literal(417),
    }),
  ],
  { title: 'ExpectationFailedResponse' },
);

export const ImATeapotResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('ImATeapotResponseEnum', ['provider/im_a_teapot']),
      status: Type.Literal(418),
    }),
  ],
  { title: 'ImATeapotResponse' },
);

export const MisdirectedRequestResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('MisdirectedRequestResponseEnum', ['provider/misdirected_request']),
      status: Type.Literal(421),
    }),
  ],
  { title: 'MisdirectedRequestResponse' },
);

export const UnprocessableEntityResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('UnprocessableEntityResponseEnum', ['provider/unprocessable_entity']),
      status: Type.Literal(422),
    }),
  ],
  { title: 'UnprocessableEntityResponseSchema' },
);

export const LockedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('LockedResponseEnum', ['provider/locked']),
      status: Type.Literal(423),
    }),
  ],
  { title: 'LockedResponse' },
);

export const FailedDependencyResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('FailedDependencyResponseEnum', ['provider/failed_dependency']),
      status: Type.Literal(424),
    }),
  ],
  { title: 'FailedDependencyResponse' },
);

export const TooEarlyResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('TooEarlyResponseEnum', ['provider/too_early']),
      status: Type.Literal(425),
    }),
  ],
  { title: 'TooEarlyResponse' },
);

export const UpgradeRequiredResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('UpgradeRequiredResponseEnum', ['provider/upgrade_required']),
      status: Type.Literal(426),
    }),
  ],
  { title: 'UpgradeRequiredResponse' },
);

export const PreconditionRequiredResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('PreconditionRequiredResponseEnum', ['provider/precondition_required']),
      status: Type.Literal(428),
    }),
  ],
  { title: 'PreconditionRequiredResponse' },
);

export const TooManyRequestsResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('TooManyRequestsResponseEnum', ['provider/too_many_requests', 'api/too_many_requests']),
      status: Type.Literal(429),
    }),
  ],
  { title: 'TooManyRequestsResponseSchema' },
);

export const RequestHeaderFieldsTooLargeResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('RequestHeaderFieldsTooLargeResponseEnum', ['provider/request_header_fields_too_large']),
      status: Type.Literal(431),
    }),
  ],
  { title: 'RequestHeaderFieldsTooLargeResponse' },
);

export const UnavailableForLegalReasonsResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('UnavailableForLegalReasonsResponseEnum', ['provider/unavailable_for_legal_reasons']),
      status: Type.Literal(451),
    }),
  ],
  { title: 'UnavailableForLegalReasonsResponse' },
);

export const InternalServerErrorResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('InternalServerErrorResponseTypeEnum', ['api/internal_error', 'provider/internal_error']),
      status: Type.Literal(500),
    }),
  ],
  { title: 'InternalServerErrorResponse', description: 'Internal Server Error' },
);

export const NotImplementedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('NotImplementedResponseEnum', ['api/not_implemented', 'provider/not_implemented']),
      status: Type.Literal(501),
    }),
  ],
  { title: 'NotImplementedErrorResponse' },
);

export const BadGatewayResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('BadGatewayResponseEnum', ['api/proxy_error', 'provider/server_error']),
      status: Type.Literal(502),
    }),
  ],
  { title: 'BadGatewayResponse' },
);

export const ServiceUnavailableResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('ServiceUnavailableResponseEnum', ['api/service_unavailable', 'provider/service_unavailable']),
      status: Type.Literal(503),
    }),
  ],
  { title: 'ServiceUnavailableResponse' },
);

export const GatewayTimeoutResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('GatewayTimeoutResponseEnum', ['api/proxy_timeout', 'provider/timeout']),
      status: Type.Literal(504),
    }),
  ],
  { title: 'GatewayTimeoutResponse' },
);

export const HttpVersionNotSupportedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('HttpVersionNotSupportedResponseEnum', ['provider/http_version_not_supported']),
      status: Type.Literal(505),
    }),
  ],
  { title: 'HttpVersionNotSupportedResponse' },
);

export const VariantAlsoNegotiatesResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('VariantAlsoNegotiatesResponseEnum', ['provider/variant_also_negotiates']),
      status: Type.Literal(506),
    }),
  ],
  { title: 'VariantAlsoNegotiatesResponse' },
);

export const InsufficientStorageResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('InsufficientStorageResponseEnum', ['provider/insufficient_storage']),
      status: Type.Literal(507),
    }),
  ],
  { title: 'InsufficientStorageResponse' },
);

export const LoopDetectedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('LoopDetectedResponseEnum', ['provider/loop_detected']),
      status: Type.Literal(508),
    }),
  ],
  { title: 'LoopDetectedResponse' },
);

export const BandwidthLimitExceededResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('BandwidthLimitExceededResponseEnum', ['provider/bandwidth_limit_exceeded']),
      status: Type.Literal(509),
    }),
  ],
  { title: 'BandwidthLimitExceededResponse' },
);

export const NotExtendedResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('NotExtendedResponseEnum', ['provider/not_extended']),
      status: Type.Literal(510),
    }),
  ],
  { title: 'NotExtendedResponse' },
);

export const NetworkAuthenticationRequiredResponseSchema = Type.Composite(
  [
    HttpErrorDetailsBaseSchema,
    Type.Object({
      type: StringEnumType('NetworkAuthenticationRequiredResponseEnum', ['provider/network_authentication_required']),
      status: Type.Literal(511),
    }),
  ],
  { title: 'NetworkAuthenticationRequiredResponse' },
);

export const ErrorResponseSchema = {
  400: BadRequestResponseSchema,
  401: UnauthorizedResponseSchema,
  402: PaymentRequiredResponseSchema,
  403: ForbiddenResponseSchema,
  404: NotFoundResponseSchema,
  405: MethodNotAllowedResponseSchema,
  406: NotAcceptableResponseSchema,
  407: ProxyAuthenticationRequiredResponseSchema,
  408: RequestTimeoutResponseSchema,
  409: ConflictResponseSchema,
  410: GoneResponseSchema,
  411: LengthRequiredResponseSchema,
  412: PreconditionFailedResponseSchema,
  413: ContentTooLargeResponseSchema,
  414: UriTooLongResponseSchema,
  415: UnsupportedMediaTypeResponseSchema,
  416: RangeNotSatisfiableResponseSchema,
  417: ExpectationFailedResponseSchema,
  418: ImATeapotResponseSchema,
  421: MisdirectedRequestResponseSchema,
  422: UnprocessableEntityResponseSchema,
  423: LockedResponseSchema,
  424: FailedDependencyResponseSchema,
  425: TooEarlyResponseSchema,
  426: UpgradeRequiredResponseSchema,
  428: PreconditionRequiredResponseSchema,
  429: TooManyRequestsResponseSchema,
  431: RequestHeaderFieldsTooLargeResponseSchema,
  451: UnavailableForLegalReasonsResponseSchema,
  500: InternalServerErrorResponseSchema,
  501: NotImplementedResponseSchema,
  502: BadGatewayResponseSchema,
  503: ServiceUnavailableResponseSchema,
  504: GatewayTimeoutResponseSchema,
  505: HttpVersionNotSupportedResponseSchema,
  506: VariantAlsoNegotiatesResponseSchema,
  507: InsufficientStorageResponseSchema,
  508: LoopDetectedResponseSchema,
  509: BandwidthLimitExceededResponseSchema,
  510: NotExtendedResponseSchema,
  511: NetworkAuthenticationRequiredResponseSchema,
};

export type HttpErrorDetails = Static<typeof HttpErrorDetailsBaseSchema>;
export type HttpErrorDetailsPayload = Pick<HttpErrorDetails, 'type' | 'title' | 'detail'>;

export function isHttpErrorDetailsPayload(data: unknown): data is HttpErrorDetailsPayload {
  if (!data || typeof data !== 'object') return false;
  if (!('type' in data) || typeof data.type !== 'string') return false;
  if (!('title' in data) || typeof data.title !== 'string') return false;

  return !('detail' in data) || data.detail === undefined || typeof data.detail === 'string';
}

export type InternalServerErrorResponse = Static<typeof InternalServerErrorResponseSchema>;
export type BadRequestResponse = Static<typeof BadRequestResponseSchema>;
export type PaymentRequiredResponse = Static<typeof PaymentRequiredResponseSchema>;
export type UnauthorizedResponse = Static<typeof UnauthorizedResponseSchema>;
export type ForbiddenResponse = Static<typeof ForbiddenResponseSchema>;
export type NotFoundResponse = Static<typeof NotFoundResponseSchema>;
export type MethodNotAllowedResponse = Static<typeof MethodNotAllowedResponseSchema>;
export type NotAcceptableResponse = Static<typeof NotAcceptableResponseSchema>;
export type ProxyAuthenticationRequiredResponse = Static<typeof ProxyAuthenticationRequiredResponseSchema>;
export type RequestTimeoutResponse = Static<typeof RequestTimeoutResponseSchema>;
export type ConflictResponse = Static<typeof ConflictResponseSchema>;
export type GoneResponse = Static<typeof GoneResponseSchema>;
export type LengthRequiredResponse = Static<typeof LengthRequiredResponseSchema>;
export type PreconditionFailedResponse = Static<typeof PreconditionFailedResponseSchema>;
export type ContentTooLargeResponse = Static<typeof ContentTooLargeResponseSchema>;
export type UriTooLongResponse = Static<typeof UriTooLongResponseSchema>;
export type UnsupportedMediaTypeResponse = Static<typeof UnsupportedMediaTypeResponseSchema>;
export type RangeNotSatisfiableResponse = Static<typeof RangeNotSatisfiableResponseSchema>;
export type ExpectationFailedResponse = Static<typeof ExpectationFailedResponseSchema>;
export type ImATeapotResponse = Static<typeof ImATeapotResponseSchema>;
export type MisdirectedRequestResponse = Static<typeof MisdirectedRequestResponseSchema>;
export type UnprocessableEntityResponse = Static<typeof UnprocessableEntityResponseSchema>;
export type LockedResponse = Static<typeof LockedResponseSchema>;
export type FailedDependencyResponse = Static<typeof FailedDependencyResponseSchema>;
export type TooEarlyResponse = Static<typeof TooEarlyResponseSchema>;
export type UpgradeRequiredResponse = Static<typeof UpgradeRequiredResponseSchema>;
export type PreconditionRequiredResponse = Static<typeof PreconditionRequiredResponseSchema>;
export type TooManyRequestsResponse = Static<typeof TooManyRequestsResponseSchema>;
export type RequestHeaderFieldsTooLargeResponse = Static<typeof RequestHeaderFieldsTooLargeResponseSchema>;
export type UnavailableForLegalReasonsResponse = Static<typeof UnavailableForLegalReasonsResponseSchema>;
export type NotImplementedResponse = Static<typeof NotImplementedResponseSchema>;
export type BadGatewayResponse = Static<typeof BadGatewayResponseSchema>;
export type ServiceUnavailableResponse = Static<typeof ServiceUnavailableResponseSchema>;
export type GatewayTimeoutResponse = Static<typeof GatewayTimeoutResponseSchema>;
export type HttpVersionNotSupportedResponse = Static<typeof HttpVersionNotSupportedResponseSchema>;
export type VariantAlsoNegotiatesResponse = Static<typeof VariantAlsoNegotiatesResponseSchema>;
export type InsufficientStorageResponse = Static<typeof InsufficientStorageResponseSchema>;
export type LoopDetectedResponse = Static<typeof LoopDetectedResponseSchema>;
export type BandwidthLimitExceededResponse = Static<typeof BandwidthLimitExceededResponseSchema>;
export type NotExtendedResponse = Static<typeof NotExtendedResponseSchema>;
export type NetworkAuthenticationRequiredResponse = Static<typeof NetworkAuthenticationRequiredResponseSchema>;

export const HttpErrorTypeSchema = Type.Union(Object.values(ErrorResponseSchema).map((schema) => schema.properties.type));

export type HttpErrorType = Static<typeof HttpErrorTypeSchema>;

export const HttpErrorTypeMap: Record<number, HttpErrorType> = {
  400: 'provider/invalid_parameters',
  401: 'provider/invalid_authorization',
  402: 'provider/payment_required',
  403: 'provider/insufficient_permissions',
  404: 'provider/resource_not_found',
  405: 'provider/method_not_allowed',
  406: 'provider/not_acceptable',
  407: 'provider/proxy_authentication_required',
  408: 'provider/request_timeout',
  409: 'provider/conflict',
  410: 'provider/gone',
  411: 'provider/length_required',
  412: 'provider/precondition_failed',
  413: 'provider/content_too_large',
  414: 'provider/uri_too_long',
  415: 'provider/invalid_file',
  416: 'provider/range_not_satisfiable',
  417: 'provider/expectation_failed',
  418: 'provider/im_a_teapot',
  421: 'provider/misdirected_request',
  422: 'provider/unprocessable_entity',
  423: 'provider/locked',
  424: 'provider/failed_dependency',
  425: 'provider/too_early',
  426: 'provider/upgrade_required',
  428: 'provider/precondition_required',
  429: 'provider/too_many_requests',
  431: 'provider/request_header_fields_too_large',
  451: 'provider/unavailable_for_legal_reasons',
  500: 'provider/internal_error',
  501: 'provider/not_implemented',
  502: 'provider/server_error',
  503: 'provider/service_unavailable',
  504: 'provider/timeout',
  505: 'provider/http_version_not_supported',
  506: 'provider/variant_also_negotiates',
  507: 'provider/insufficient_storage',
  508: 'provider/loop_detected',
  509: 'provider/bandwidth_limit_exceeded',
  510: 'provider/not_extended',
  511: 'provider/network_authentication_required',
};
